import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, Truck as TruckIcon, PackageX, Pencil, Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { computeLoadGross } from '@/lib/invoiceHelpers';
import { computeTripStops } from '@/lib/trips';
import TripFormModal from './TripFormModal';

const TRIP_TYPE_STYLES = {
  Company: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Broker: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  'Empty Move': 'text-muted-foreground bg-muted/40 border-border',
};
const STATUS_STYLES = {
  Planned: 'text-muted-foreground bg-muted/40 border-border',
  Dispatched: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  'In Transit': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Completed: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
};

export default function TripsTab() {
  const [trips, setTrips] = useState([]);
  const [tripLoads, setTripLoads] = useState([]);
  const [loadsById, setLoadsById] = useState(new Map());
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [modalMode, setModalMode] = useState('trip'); // 'trip' | 'empty-move'

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [tripsData, linksData, loadsData, driverData, assetData] = await Promise.all([
      client.entities.Trip.list('-created_date', 300).catch(() => []),
      client.entities.TripLoad.list('-created_date', 1000).catch(() => []),
      client.entities.Load.list('-created_date', 1000).catch(() => []),
      client.entities.DriverProfile.filter({ is_active: true }, 'full_name', 200).catch(() => []),
      client.entities.Asset.list('-created_date', 200).catch(() => []),
    ]);
    setTrips(tripsData);
    setTripLoads(linksData);
    setLoadsById(new Map(loadsData.map(l => [l.id, l])));
    setDrivers(driverData);
    const active = assetData.filter(a => a.is_active !== false);
    setTrucks(active.filter(a => a.asset_type === 'Truck'));
    setTrailers(active.filter(a => a.asset_type === 'Trailer'));
    setLoading(false);
  };

  const linksByTrip = useMemo(() => {
    const map = new Map();
    for (const link of tripLoads) {
      if (!map.has(link.trip_id)) map.set(link.trip_id, []);
      map.get(link.trip_id).push(link);
    }
    return map;
  }, [tripLoads]);

  const truckByIdMap = useMemo(() => new Map(trucks.map(t => [t.id, t])), [trucks]);

  const today = new Date().toISOString().split('T')[0];
  const getTiming = (t) => {
    if (!t.delivery_date) return 'past';
    if (t.delivery_date > today) return 'future';
    if (t.delivery_date === today) return 'today';
    return 'past';
  };

  const visibleTrips = trips.filter(t => {
    const timeOk = timeFilter === 'All' ||
      (timeFilter === 'Future' && getTiming(t) === 'future') ||
      (timeFilter === 'Today' && getTiming(t) === 'today') ||
      (timeFilter === 'Past' && getTiming(t) === 'past');
    if (!timeOk) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const links = linksByTrip.get(t.id) || [];
    const loadNums = links.map(l => loadsById.get(l.load_id)?.load_number).filter(Boolean).join(' ');
    return [t.trip_number, t.assigned_driver_name, t.origin, t.destination, loadNums]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  const tripRevenue = (tripId) => {
    const links = linksByTrip.get(tripId) || [];
    return links.reduce((sum, l) => sum + computeLoadGross(loadsById.get(l.load_id) || {}), 0);
  };

  const tripStopsCount = (tripId) => {
    const links = linksByTrip.get(tripId) || [];
    return computeTripStops(links.map(l => loadsById.get(l.load_id)).filter(Boolean));
  };

  const openNewTrip = () => { setEditTrip(null); setModalMode('trip'); setShowModal(true); };
  const openEmptyMove = () => { setEditTrip(null); setModalMode('empty-move'); setShowModal(true); };
  const openEdit = (trip) => { setEditTrip(trip); setModalMode('trip'); setShowModal(true); };

  const handleDelete = async (trip) => {
    if (!confirm(`Delete Trip ${trip.trip_number}? This also unlinks (but does not delete) any attached loads.`)) return;
    try {
      await client.entities.Trip.delete(trip.id);
      toast.success('Trip deleted.');
      loadData();
    } catch (e) {
      toast.error('Failed to delete trip.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {['All', 'Future', 'Today', 'Past'].map(f => (
            <button
              key={f}
              onClick={() => setTimeFilter(f)}
              className={cn(
                "text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                timeFilter === f
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {f === 'All' ? 'All' : `${f} Trip`}
            </button>
          ))}
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search trip #, driver, load #, lane…"
            className="w-64 h-8 text-xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openEmptyMove} variant="outline" size="sm" className="h-8 text-xs">
            <PackageX className="w-3.5 h-3.5 mr-1.5" /> Empty Move
          </Button>
          <Button onClick={openNewTrip} size="sm" className="h-8 text-xs bg-primary text-primary-foreground">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Trip
          </Button>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Trip #</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Type</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Status</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Pickup</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Delivery</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Driver</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Truck</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">From</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">To</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Equipment</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Stops</th>
                <th className="text-left px-3 py-2.5 text-muted-foreground font-medium">Loads</th>
                <th className="text-right px-3 py-2.5 text-muted-foreground font-medium">Total</th>
                <th className="text-right px-3 py-2.5 text-muted-foreground font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleTrips.map(t => {
                const links = linksByTrip.get(t.id) || [];
                const revenue = tripRevenue(t.id);
                const stops = tripStopsCount(t.id);
                const truck = truckByIdMap.get(t.truck_id);
                return (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-white/3 transition-colors">
                    <td className="px-3 py-2.5 font-semibold text-foreground">{t.trip_number}</td>
                    <td className="px-3 py-2.5">
                      <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", TRIP_TYPE_STYLES[t.trip_type] || TRIP_TYPE_STYLES.Company)}>
                        {t.trip_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", STATUS_STYLES[t.status] || STATUS_STYLES.Planned)}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{t.pickup_date || '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{t.delivery_date || '—'}</td>
                    <td className="px-3 py-2.5 text-foreground">{t.assigned_driver_name || '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{truck?.unit_number || '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {t.origin_name && <div className="text-foreground text-xs">{t.origin_name}</div>}
                      {t.origin || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {t.destination_name && <div className="text-foreground text-xs">{t.destination_name}</div>}
                      {t.destination || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{t.equipment_type || '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{stops || '—'}</td>
                    <td className="px-3 py-2.5">
                      {links.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-foreground">
                          <Package className="w-3 h-3 text-muted-foreground" />
                          {links.length} load{links.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-foreground">
                      {revenue > 0 ? `$${revenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(t)} className="p-1.5 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(t)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleTrips.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-3 py-10 text-center text-muted-foreground text-sm">
                    <TruckIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    No trips found. Create one to assign a driver/truck to one or more loads.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <TripFormModal
          trip={editTrip}
          mode={modalMode}
          drivers={drivers}
          trucks={trucks}
          trailers={trailers}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadData(); }}
        />
      )}
    </div>
  );
}
